import React, { useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  person_type: 'PF',
  full_name: '',
  email: '',
  phone: '',
  company_name: '',
  contact_person: '',
  notes: '',
};

export const NewClientDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState(null);

  const reset = () => {
    setForm(EMPTY);
    setDuplicates(null);
    setSaving(false);
  };

  const handleOpenChange = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const submit = async (force) => {
    if (!form.full_name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      await api.post('/clients', {
        full_name: form.full_name.trim(),
        email: form.email || null,
        phone: form.phone || null,
        company_name: form.company_name || null,
        contact_person: form.contact_person || null,
        person_type: form.person_type,
        notes: form.notes || null,
        force,
      });
      toast.success('Cliente cadastrado');
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicates) {
        setDuplicates(detail.duplicates);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao cadastrar cliente');
      }
    } finally {
      setSaving(false);
    }
  };

  const isPJ = form.person_type === 'PJ';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Novo Cliente</DialogTitle>
          <DialogDescription className="sr-only">Formulário de cadastro de novo cliente</DialogDescription>
        </DialogHeader>

        {duplicates && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="duplicate-warning">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" />
              Já existe cliente com nome igual ou parecido
            </div>
            <ul className="mt-2 space-y-1">
              {duplicates.map((d) => (
                <li key={d.user_id} className="text-xs text-amber-900 bg-amber-100/60 rounded px-2 py-1">
                  <span className="font-medium">{d.full_name}</span>
                  {d.email ? ` · ${d.email}` : ''}
                  {` · ${d.project_count} projeto${d.project_count !== 1 ? 's' : ''}`}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-amber-700 mt-2">
              Revise os cadastros acima. Se tiver certeza de que é um cliente diferente, confirme para salvar mesmo assim.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="md:col-span-2">
            <Label>Tipo</Label>
            <Select value={form.person_type} onValueChange={(v) => setForm({ ...form, person_type: v })}>
              <SelectTrigger data-testid="new-person-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PF">Pessoa Física</SelectItem>
                <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>{isPJ ? 'Razão Social *' : 'Nome Completo *'}</Label>
            <Input
              value={form.full_name}
              onChange={(e) => { setForm({ ...form, full_name: e.target.value }); setDuplicates(null); }}
              data-testid="new-full-name"
            />
          </div>
          {isPJ && (
            <>
              <div>
                <Label>Nome Fantasia</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              </div>
              <div>
                <Label>Pessoa de Contato</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
              </div>
            </>
          )}
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-email" />
          </div>
          <div>
            <Label>Telefone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>Observações</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
          {duplicates ? (
            <Button
              onClick={() => submit(true)}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="btn-save-anyway"
            >
              {saving ? 'Salvando...' : 'Salvar mesmo assim'}
            </Button>
          ) : (
            <Button
              onClick={() => submit(false)}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="btn-save-new-client"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewClientDialog;
